import { Injectable, signal } from '@angular/core';

export type ExportFormat = 'pdf' | 'excel' | 'csv';

export interface ExportProgress {
  isExporting: boolean;
  progress: number;
  message: string;
}

export interface ExportOptions {
  format: ExportFormat;
  searchCriteria: any;
  results: any[];
  searchType: 'service' | 'resource';
  fileName?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ExportService {
  private exportProgress = signal<ExportProgress>({
    isExporting: false,
    progress: 0,
    message: ''
  });

  readonly progress = this.exportProgress.asReadonly();

  async exportResults(options: ExportOptions): Promise<void> {
    this.updateProgress(true, 0, 'Iniciando exportação...');

    try {
      switch (options.format) {
        case 'pdf':
          await this.exportToPDF(options);
          break;
        case 'excel':
          await this.exportToExcel(options);
          break;
        case 'csv':
          await this.exportToCSV(options);
          break;
      }
      
      this.updateProgress(false, 100, 'Exportação concluída!');
    } catch (error) {
      console.error('Erro ao exportar:', error);
      this.updateProgress(false, 0, 'Erro na exportação');
      throw error;
    }
  }

  private async exportToPDF(options: ExportOptions): Promise<void> {
    this.updateProgress(true, 10, 'Preparando documento PDF...');
    
    // Dynamically import jsPDF
    const { jsPDF } = await import('jspdf');
    await import('jspdf-autotable');
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let yPosition = 20;

    // Título
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    const title = options.searchType === 'service' 
      ? 'Resultados da Busca - Serviços' 
      : 'Resultados da Busca - Recursos';
    doc.text(title, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 10;

    this.updateProgress(true, 30, 'Adicionando critérios de busca...');

    // Critérios de Busca
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Critérios de Busca:', 14, yPosition);
    yPosition += 7;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const criteria = this.formatSearchCriteria(options.searchCriteria);
    criteria.forEach(criterion => {
      if (yPosition > 270) {
        doc.addPage();
        yPosition = 20;
      }
      doc.text(`• ${criterion}`, 14, yPosition);
      yPosition += 5;
    });

    yPosition += 5;

    this.updateProgress(true, 50, 'Adicionando resultados...');

    // Resultados
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(`Resultados (${options.results.length} encontrados):`, 14, yPosition);
    yPosition += 7;

    // Tabela de resultados
    const tableData = this.prepareTableData(options.results, options.searchType);
    
    (doc as any).autoTable({
      startY: yPosition,
      head: [tableData.headers],
      body: tableData.rows,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      margin: { top: 10, left: 14, right: 14 },
      didDrawPage: (data: any) => {
        // Footer com número de página
        const pageCount = (doc as any).internal.getNumberOfPages();
        doc.setFontSize(8);
        doc.text(
          `Página ${data.pageNumber} de ${pageCount}`,
          pageWidth / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: 'center' }
        );
      }
    });

    this.updateProgress(true, 90, 'Salvando arquivo...');

    // Salvar arquivo
    const fileName = options.fileName || `resultados_busca_${Date.now()}.pdf`;
    doc.save(fileName);
  }

  private async exportToExcel(options: ExportOptions): Promise<void> {
    this.updateProgress(true, 10, 'Preparando planilha Excel...');
    
    // Dynamically import xlsx
    const XLSX = await import('xlsx');
    
    this.updateProgress(true, 30, 'Formatando dados...');

    // Criar workbook
    const wb = XLSX.utils.book_new();

    // Sheet 1: Critérios de Busca
    const criteriaData = this.formatSearchCriteriaForExcel(options.searchCriteria);
    const criteriaWs = XLSX.utils.aoa_to_sheet(criteriaData);
    XLSX.utils.book_append_sheet(wb, criteriaWs, 'Critérios de Busca');

    this.updateProgress(true, 60, 'Adicionando resultados...');

    // Sheet 2: Resultados
    const resultsData = this.prepareExcelData(options.results, options.searchType);
    const resultsWs = XLSX.utils.json_to_sheet(resultsData);
    XLSX.utils.book_append_sheet(wb, resultsWs, 'Resultados');

    this.updateProgress(true, 90, 'Salvando arquivo...');

    // Salvar arquivo
    const fileName = options.fileName || `resultados_busca_${Date.now()}.xlsx`;
    XLSX.writeFile(wb, fileName);
  }

  private async exportToCSV(options: ExportOptions): Promise<void> {
    this.updateProgress(true, 10, 'Preparando arquivo CSV...');
    
    // Dynamically import xlsx for CSV export
    const XLSX = await import('xlsx');
    
    this.updateProgress(true, 50, 'Formatando dados...');

    // Preparar dados
    const data = this.prepareExcelData(options.results, options.searchType);
    
    // Criar worksheet
    const ws = XLSX.utils.json_to_sheet(data);
    
    // Criar workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Resultados');

    this.updateProgress(true, 90, 'Salvando arquivo...');

    // Salvar como CSV
    const fileName = options.fileName || `resultados_busca_${Date.now()}.csv`;
    XLSX.writeFile(wb, fileName, { bookType: 'csv' });
  }

  private formatSearchCriteria(criteria: any): string[] {
    const formatted: string[] = [];

    if (!criteria) return formatted;

    // Informações Profissionais
    if (criteria.councilGroup) {
      formatted.push(`Conselho: ${criteria.councilGroup}`);
    }
    if (criteria.professionName) {
      formatted.push(`Profissão: ${criteria.professionName}`);
    }
    if (criteria.specialty) {
      formatted.push(`Especialidade: ${criteria.specialty}`);
    }
    if (criteria.activityAreas && criteria.activityAreas.length > 0) {
      formatted.push(`Áreas de Atuação: ${criteria.activityAreas.join(', ')}`);
    }

    // Tipo de Atendimento
    const careTypes: string[] = [];
    if (criteria.adultCare) careTypes.push('Adulto');
    if (criteria.pediatricCare) careTypes.push('Pediátrico');
    if (careTypes.length > 0) {
      formatted.push(`Tipo de Atendimento: ${careTypes.join(', ')}`);
    }

    // Formato de Serviço
    const serviceFormats: string[] = [];
    if (criteria.serviceFormatOnLine) serviceFormats.push('Online');
    if (criteria.serviceFormatPresential) serviceFormats.push('Presencial');
    if (serviceFormats.length > 0) {
      formatted.push(`Formato: ${serviceFormats.join(', ')}`);
    }

    // Localização
    if (criteria.state) {
      formatted.push(`Estado: ${criteria.state}`);
    }
    if (criteria.referenceCity) {
      formatted.push(`Cidade de Referência: ${criteria.referenceCity}`);
    }
    if (criteria.radiusInKm) {
      formatted.push(`Raio: ${criteria.radiusInKm} km`);
    }
    if (criteria.selectedCities && criteria.selectedCities.length > 0) {
      formatted.push(`Cidades Selecionadas: ${criteria.selectedCities.length}`);
    }

    // Valores
    if (criteria.amountMin || criteria.amountMax) {
      const min = criteria.amountMin ? `R$ ${criteria.amountMin}` : 'Não especificado';
      const max = criteria.amountMax ? `R$ ${criteria.amountMax}` : 'Não especificado';
      formatted.push(`Faixa de Preço: ${min} - ${max}`);
    }

    // Recursos (para busca de exames/terapias)
    if (criteria.resourceCategory) {
      formatted.push(`Categoria de Recurso: ${criteria.resourceCategory}`);
    }

    // Datas
    if (criteria.publicationDateFrom) {
      formatted.push(`Data de Publicação (De): ${new Date(criteria.publicationDateFrom).toLocaleDateString('pt-BR')}`);
    }
    if (criteria.publicationDateTo) {
      formatted.push(`Data de Publicação (Até): ${new Date(criteria.publicationDateTo).toLocaleDateString('pt-BR')}`);
    }

    return formatted;
  }

  private formatSearchCriteriaForExcel(criteria: any): any[][] {
    const data: any[][] = [
      ['Critérios de Busca'],
      [''],
      ['Campo', 'Valor']
    ];

    const formatted = this.formatSearchCriteria(criteria);
    formatted.forEach(criterion => {
      const [field, value] = criterion.split(': ');
      data.push([field, value || '']);
    });

    return data;
  }

  private prepareTableData(results: any[], searchType: 'service' | 'resource'): { headers: string[], rows: any[][] } {
    if (searchType === 'service') {
      return {
        headers: ['ID', 'Profissão', 'Especialidade', 'Cidade/Estado', 'Valor', 'Status'],
        rows: results.map(item => [
          item.id || '',
          item.professionName || '',
          item.specialty || '',
          item.address ? `${item.address.city}/${item.address.state}` : '',
          item.amount ? `R$ ${item.amount.toFixed(2)}` : '',
          item.status || ''
        ])
      };
    } else {
      return {
        headers: ['ID', 'Recursos', 'Cidade/Estado', 'Data de Criação', 'Status'],
        rows: results.map(item => [
          item.id || '',
          item.resource ? item.resource.join(', ') : '',
          item.address ? `${item.address.city}/${item.address.state}` : '',
          item.creationDate ? new Date(item.creationDate).toLocaleDateString('pt-BR') : '',
          item.status || ''
        ])
      };
    }
  }

  private prepareExcelData(results: any[], searchType: 'service' | 'resource'): any[] {
    if (searchType === 'service') {
      return results.map(item => ({
        'ID': item.id || '',
        'Profissão': item.professionName || '',
        'Especialidade': item.specialty || '',
        'Áreas de Atuação': item.activityAreas || '',
        'Atendimento Adulto': item.adultCare ? 'Sim' : 'Não',
        'Atendimento Pediátrico': item.pediatricCare ? 'Sim' : 'Não',
        'Formato Online': item.serviceFormatOnLine ? 'Sim' : 'Não',
        'Formato Presencial': item.serviceFormatPresential ? 'Sim' : 'Não',
        'Endereço': item.address ? `${item.address.street}, ${item.address.number}` : '',
        'Cidade': item.address?.city || '',
        'Estado': item.address?.state || '',
        'Valor': item.amount || '',
        'Unidade de Cobrança': item.billingUnit || '',
        'Descrição': item.description || '',
        'Status': item.status || '',
        'Atendimento Plantonista': item.emergencyService ? 'Sim' : 'Não',
        'Data de Criação': item.creationDate ? new Date(item.creationDate).toLocaleDateString('pt-BR') : '',
        'Data de Publicação': item.publicationDate ? new Date(item.publicationDate).toLocaleDateString('pt-BR') : ''
      }));
    } else {
      return results.map(item => ({
        'ID': item.id || '',
        'Recursos': item.resource ? item.resource.join(', ') : '',
        'Endereço': item.address ? `${item.address.street}, ${item.address.number}` : '',
        'Cidade': item.address?.city || '',
        'Estado': item.address?.state || '',
        'Data de Criação': item.creationDate ? new Date(item.creationDate).toLocaleDateString('pt-BR') : '',
        'Data de Publicação': item.publicationDate ? new Date(item.publicationDate).toLocaleDateString('pt-BR') : '',
        'Status': item.status || ''
      }));
    }
  }

  private updateProgress(isExporting: boolean, progress: number, message: string): void {
    this.exportProgress.set({ isExporting, progress, message });
  }
}
